// NeedSeedPanel — the in-canvas "let Link draft this annons for me" affordance.
//
// Mirrors LinkSeedPanel (the module form's seed). Describe the role + optional
// context (and optionally ground in uploaded material); Link returns a full
// GeneratedNeed that the create form loads into its fields + Markdown editor.
// Nothing is persisted here — the user edits and then saves the annons.

import React, { useEffect, useRef, useState } from 'react';
import {
    Loader2, Upload, Link as LinkIcon, File as FileIcon, FileText, Image as ImageIcon,
    Trash2, Globe, AlertCircle, ChevronDown, Paperclip,
} from 'lucide-react';
import { needApi, GeneratedNeed } from '@/lib/api/needApi';
import { sourcesApi } from '@/lib/api/sourcesApi';
import {
    Source, SourceKindLabels, SOURCE_ALLOWED_EXTENSIONS, SOURCE_ALLOWED_SPECIAL_NAMES, SOURCE_MAX_FILE_BYTES,
} from '@/lib/models/source';
import { EmploymentType, EmploymentExtent, EmploymentTypeLabels, EmploymentExtentLabels } from '@/lib/models/need';
import { LinkAvatar } from '@/components/icons/LinkAvatar';
import { InlineSelect } from '@/components/custom-ui';

interface NeedSeedPanelProps {
    onSeeded: (generated: GeneratedNeed) => void;
    hasContent: boolean;
    defaultOpen?: boolean;
}

const PRETTY_BYTES = (n: number | null) => {
    if (!n) return '';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const NeedSeedPanel: React.FC<NeedSeedPanelProps> = ({ onSeeded, hasContent, defaultOpen }) => {
    const [open, setOpen] = useState(defaultOpen ?? false);

    const [topic, setTopic] = useState('');
    const [employmentType, setEmploymentType] = useState<EmploymentType | ''>('');
    const [employmentExtent, setEmploymentExtent] = useState<EmploymentExtent | ''>('');
    const [additionalNotes, setAdditionalNotes] = useState('');
    const [isGenerating, setIsGenerating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Optional grounding sources
    const [showSources, setShowSources] = useState(false);
    const [sources, setSources] = useState<Source[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [isLoadingSources, setIsLoadingSources] = useState(false);
    const [sourcesLoaded, setSourcesLoaded] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [urlInput, setUrlInput] = useState('');
    const [isAddingURL, setIsAddingURL] = useState(false);
    const [sourceError, setSourceError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const dragCounter = useRef(0);

    useEffect(() => {
        if (!showSources || sourcesLoaded) return;
        let cancelled = false;
        setIsLoadingSources(true);
        (async () => {
            try {
                const list = await sourcesApi.listSources();
                if (!cancelled) { setSources(list); setSourcesLoaded(true); }
            } catch (err: any) {
                if (!cancelled) setSourceError(err?.message || 'Kunde inte ladda källor.');
            } finally {
                if (!cancelled) setIsLoadingSources(false);
            }
        })();
        return () => { cancelled = true; };
    }, [showSources, sourcesLoaded]);

    const toggleSelected = (id: string) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const uploadFile = async (file: File) => {
        const lastDot = file.name.lastIndexOf('.');
        const ext = lastDot > 0 ? '.' + file.name.slice(lastDot + 1).toLowerCase() : '';
        const allowedByExt = ext && SOURCE_ALLOWED_EXTENSIONS.includes(ext as any);
        const allowedBySpecial = SOURCE_ALLOWED_SPECIAL_NAMES.has(file.name.toLowerCase());
        if (!allowedByExt && !allowedBySpecial) {
            setSourceError(`${ext ? `Filtypen ${ext}` : `Filen ${file.name}`} stöds inte. Tillåtna: PDF, bilder, text- och kodfiler.`);
            return;
        }
        if (file.size > SOURCE_MAX_FILE_BYTES) {
            setSourceError(`Filen är för stor (${PRETTY_BYTES(file.size)}). Max ${PRETTY_BYTES(SOURCE_MAX_FILE_BYTES)}.`);
            return;
        }
        setIsUploading(true);
        setSourceError(null);
        try {
            const uploaded = await sourcesApi.uploadSource(file);
            setSources((prev) => [uploaded, ...prev]);
            setSelectedIds((prev) => new Set(prev).add(uploaded.id));
        } catch (err: any) {
            setSourceError(err?.message || 'Uppladdningen misslyckades.');
        } finally {
            setIsUploading(false);
        }
    };

    const handleFilePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (file) await uploadFile(file);
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault(); e.stopPropagation();
        if (e.dataTransfer?.types?.includes('Files')) { dragCounter.current += 1; setIsDragging(true); }
    };
    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault(); e.stopPropagation();
        dragCounter.current = Math.max(0, dragCounter.current - 1);
        if (dragCounter.current === 0) setIsDragging(false);
    };
    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault(); e.stopPropagation();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'copy';
    };
    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault(); e.stopPropagation();
        dragCounter.current = 0; setIsDragging(false);
        const file = e.dataTransfer?.files?.[0];
        if (file) await uploadFile(file);
    };

    const handleAddURL = async () => {
        const trimmed = urlInput.trim();
        if (!trimmed) return;
        if (!/^https?:\/\//i.test(trimmed)) {
            setSourceError('URL:en måste börja med http:// eller https://');
            return;
        }
        setIsAddingURL(true);
        setSourceError(null);
        try {
            const created = await sourcesApi.createURLSource({ url: trimmed });
            setSources((prev) => [created, ...prev]);
            setSelectedIds((prev) => new Set(prev).add(created.id));
            setUrlInput('');
        } catch (err: any) {
            setSourceError(err?.message || 'Kunde inte lägga till länk.');
        } finally {
            setIsAddingURL(false);
        }
    };

    const handleDeleteSource = async (id: string) => {
        if (!window.confirm('Ta bort denna källa? Den försvinner från alla val.')) return;
        try {
            await sourcesApi.deleteSource(id);
            setSources((prev) => prev.filter((s) => s.id !== id));
            setSelectedIds((prev) => { const next = new Set(prev); next.delete(id); return next; });
        } catch (err: any) {
            setSourceError(err?.message || 'Kunde inte ta bort källan.');
        }
    };

    const handleGenerate = async () => {
        if (!topic.trim()) {
            setError('Beskriv vilken roll annonsen ska handla om.');
            return;
        }
        if (hasContent && !window.confirm('Detta ersätter det du redan fyllt i. Vill du fortsätta?')) {
            return;
        }
        setIsGenerating(true);
        setError(null);
        try {
            const generated = await needApi.aiGenerateNeed({
                topic: topic.trim(),
                employment_type: employmentType || undefined,
                employment_extent: employmentExtent || undefined,
                additional_notes: additionalNotes.trim() || undefined,
                source_ids: selectedIds.size > 0 ? Array.from(selectedIds) : undefined,
            });
            onSeeded(generated);
            setOpen(false);
        } catch (err: any) {
            setError(err?.message || 'Link kunde inte skapa utkastet. Försök igen.');
        } finally {
            setIsGenerating(false);
        }
    };

    const renderSourceIcon = (kind: Source['kind']) => {
        if (kind === 'pdf') return <FileIcon className="w-4 h-4 text-rose-600" />;
        if (kind === 'image') return <ImageIcon className="w-4 h-4 text-violet-600" />;
        if (kind === 'text') return <FileText className="w-4 h-4 text-emerald-600" />;
        return <Globe className="w-4 h-4 text-sky-600" />;
    };

    return (
        <div className="rounded-2xl border border-gray-200 bg-white overflow-hidden">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="w-full flex items-center gap-2.5 p-3.5 text-left hover:bg-gray-50 transition-colors"
            >
                <LinkAvatar className="w-6 h-6 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-gray-900">Låt Link skapa ett utkast</p>
                    <p className="text-xs text-gray-500">Beskriv rollen så bygger Link en första version av annonsen — redigera fritt efteråt.</p>
                </div>
                <ChevronDown className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>

            {open && (
                <div className="p-3.5 pt-0 space-y-3">
                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                            Vilken roll söker ni? <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            value={topic}
                            onChange={(e) => setTopic(e.target.value)}
                            placeholder="t.ex. Socialsekreterare till barn- och ungdomsenheten, heltid, erfarenhet av myndighetsutövning."
                            className="w-full h-20 p-3 bg-white border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#1A4D4F]"
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Anställningsform (valfritt)</label>
                            <InlineSelect
                                variant="outline"
                                ariaLabel="Anställningsform"
                                placeholder="Låt Link välja"
                                value={employmentType}
                                onChange={(v) => setEmploymentType(v as EmploymentType | '')}
                                options={[
                                    { value: '', label: 'Låt Link välja' },
                                    ...(Object.keys(EmploymentTypeLabels) as EmploymentType[]).map((t) => ({ value: t, label: EmploymentTypeLabels[t] })),
                                ]}
                            />
                        </div>
                        <div>
                            <label className="block text-xs font-medium text-gray-700 mb-1">Omfattning (valfritt)</label>
                            <InlineSelect
                                variant="outline"
                                ariaLabel="Omfattning"
                                placeholder="Låt Link välja"
                                value={employmentExtent}
                                onChange={(v) => setEmploymentExtent(v as EmploymentExtent | '')}
                                options={[
                                    { value: '', label: 'Låt Link välja' },
                                    ...(Object.keys(EmploymentExtentLabels) as EmploymentExtent[]).map((x) => ({ value: x, label: EmploymentExtentLabels[x] })),
                                ]}
                            />
                        </div>
                    </div>

                    <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">Övriga noteringar (valfritt)</label>
                        <input
                            type="text"
                            value={additionalNotes}
                            onChange={(e) => setAdditionalNotes(e.target.value)}
                            placeholder="t.ex. handledning erbjuds, distans möjligt, lön enligt avtal"
                            className="w-full p-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D4F]"
                        />
                    </div>

                    {/* Optional grounding sources */}
                    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
                        <button
                            type="button"
                            onClick={() => setShowSources((v) => !v)}
                            className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
                        >
                            <Paperclip className="w-4 h-4 text-gray-500 flex-shrink-0" />
                            <span className="text-xs font-medium text-gray-700 flex-1">
                                Grunda i material (valfritt)
                                {selectedIds.size > 0 && <span className="ml-1 text-[#1A4D4F]">· {selectedIds.size} vald{selectedIds.size === 1 ? '' : 'a'}</span>}
                            </span>
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${showSources ? 'rotate-180' : ''}`} />
                        </button>

                        {showSources && (
                            <div className="px-3 pb-3 space-y-2 border-t border-gray-100 pt-3">
                                <input ref={fileInputRef} type="file" accept={SOURCE_ALLOWED_EXTENSIONS.join(',')} onChange={handleFilePick} className="hidden" />
                                <button
                                    type="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    onDragEnter={handleDragEnter}
                                    onDragLeave={handleDragLeave}
                                    onDragOver={handleDragOver}
                                    onDrop={handleDrop}
                                    disabled={isUploading}
                                    className={`w-full flex items-center justify-center gap-2 p-3 border-2 border-dashed rounded-xl text-xs transition-all disabled:opacity-60 ${
                                        isDragging ? 'border-[#1A4D4F] bg-[#A4C3B2]/40 text-[#1A4D4F]' : 'border-gray-300 hover:border-[#1A4D4F] text-gray-600'
                                    }`}
                                >
                                    {isUploading ? (<><Loader2 className="w-4 h-4 animate-spin" /> Laddar upp…</>)
                                        : isDragging ? (<><Upload className="w-4 h-4" /> Släpp filen här</>)
                                            : (<><Upload className="w-4 h-4" /> Dra hit en fil eller klicka (PDF/bild, max 10 MB)</>)}
                                </button>
                                <div className="flex gap-2">
                                    <input
                                        type="url"
                                        value={urlInput}
                                        onChange={(e) => setUrlInput(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddURL(); } }}
                                        placeholder="https://example.com/befattningsbeskrivning"
                                        className="flex-1 p-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D4F]"
                                    />
                                    <button
                                        type="button"
                                        onClick={handleAddURL}
                                        disabled={isAddingURL || !urlInput.trim()}
                                        className="px-3 flex items-center gap-1.5 bg-[#1A4D4F] hover:bg-[#1A4D4F]/90 disabled:opacity-50 text-white rounded-xl text-xs font-medium"
                                    >
                                        {isAddingURL ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />} Lägg till
                                    </button>
                                </div>

                                {isLoadingSources ? (
                                    <div className="text-xs text-gray-500 py-1">Laddar källor…</div>
                                ) : sources.length === 0 ? (
                                    <p className="text-xs text-gray-400 italic">Inga källor ännu — ladda upp eller länka ovan.</p>
                                ) : (
                                    <ul className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-44 overflow-y-auto">
                                        {sources.map((s) => {
                                            const selected = selectedIds.has(s.id);
                                            return (
                                                <li
                                                    key={s.id}
                                                    className={`flex items-center gap-2.5 p-2.5 cursor-pointer transition-colors ${selected ? 'bg-[#A4C3B2]/20' : 'hover:bg-gray-50'}`}
                                                    onClick={() => toggleSelected(s.id)}
                                                >
                                                    <input type="checkbox" checked={selected} onChange={() => toggleSelected(s.id)} onClick={(e) => e.stopPropagation()} className="w-4 h-4 accent-[#1A4D4F]" />
                                                    {renderSourceIcon(s.kind)}
                                                    <div className="flex-1 min-w-0">
                                                        <div className="text-xs font-medium text-gray-900 truncate">{s.original_name}</div>
                                                        <div className="text-[11px] text-gray-500 flex items-center gap-1.5">
                                                            <span>{SourceKindLabels[s.kind]}</span>
                                                            {s.size_bytes && <span>· {PRETTY_BYTES(s.size_bytes)}</span>}
                                                        </div>
                                                    </div>
                                                    <button type="button" onClick={(e) => { e.stopPropagation(); handleDeleteSource(s.id); }} title="Ta bort" className="p-1 hover:bg-red-100 hover:text-red-600 text-gray-400 rounded-lg transition-colors">
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </button>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}

                                {sourceError && (
                                    <div className="flex items-start gap-1.5 text-[11px] text-red-600">
                                        <AlertCircle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                        <span>{sourceError}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {error && <p className="text-xs text-red-600">{error}</p>}

                    <button
                        type="button"
                        onClick={handleGenerate}
                        disabled={isGenerating || !topic.trim()}
                        className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl bg-[#1A4D4F] text-white hover:bg-[#143a3c] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                    >
                        {isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkAvatar className="w-4 h-4" bg="transparent" fg="#FFFFFF" />}
                        {isGenerating ? 'Link skapar utkast…' : 'Skapa utkast'}
                    </button>
                </div>
            )}
        </div>
    );
};

export default NeedSeedPanel;
